import { ACTION_REGISTRY, type ActionName } from './_generated-registry';
import type { ActionContext } from './types';
import { parseActionNames, parseJsonProp } from '../utils/helpers/hydration';
import actionsConfig from './actions.config.json';

const { actionsKey, dataKey } = actionsConfig.actionProps;

/**
 * Run the actions declared on an element.
 *
 * One implementation for every caller. `me-button` uses it for the actions bound to itself;
 * section components that own a wrapper around a slotted button use it for the actions they stamp
 * on that wrapper, because the wrapper is a plain element and has no component of its own to run
 * them. Both paths previously carried their own copy of this loop.
 *
 * Reads BOTH the attribute and the property and merges them, rather than letting one win. The two
 * are different authors, not two spellings of the same thing: the property is what the component
 * bound to itself, the attribute is what the page payload asked for. Shadowing meant that adding
 * an action from the template builder silently removed the component's own behaviour — the author
 * thinks they added tracking to a CTA and instead they have turned the CTA off. Component-declared
 * names run first and duplicates collapse, so a payload that repeats a name does not run it twice.
 *
 * On a server-rendered page the attribute is also the only form a plain wrapper element can carry;
 * the property form still matters for a JSX or Storybook binding that never reaches the DOM.
 *
 * Chain semantics, unchanged from the original in me-button: sequential and awaited in array order;
 * an unknown action is warned about and skipped; a throwing action aborts the rest. An action that
 * navigates therefore ends the chain — put it last.
 */
export async function runElementActions(
  el: HTMLElement | undefined | null,
  event: Event,
  component?: unknown,
  /**
   * Element handed to the action as `context.element`, when it differs from the one the
   * declaration was read from. me-button needs this: the actions are declared on its host (that is
   * where the attribute reflects and where the prop lives), but an action should act on the inner
   * element it actually rendered — that is what carries the button styling and receives the click.
   */
  contextElement?: HTMLElement | null,
  /** Names to drop, because a nested trigger is already running them. See `runWrapperActions`. */
  exclude?: string[],
): Promise<void> {
  if (!el) return;

  const acting = contextElement ?? el;
  const target = el as HTMLElement & { triggerActions?: unknown; actionData?: unknown };

  const names = mergeNames(
    parseActionNames(target.triggerActions),
    parseActionNames(target.getAttribute(actionsKey)),
  ).filter((name) => !exclude?.includes(name)) as ActionName[];
  if (!names.length) return;

  const data = {
    ...readData(target.actionData),
    // The payload is the more specific author: it configured this page, the component configured
    // every page. On a key both supplied, it wins.
    ...readData(target.getAttribute(dataKey)),
  };

  try {
    for (const name of names) {
      await runSingleAction(name, { event, element: acting, component, data }, data);
    }
  } catch (error) {
    console.error('Action execution failed:', error);
  }
}

/** Component-declared names first, then payload-declared, each name at most once. */
function mergeNames(own: string[], fromPayload: string[]): string[] {
  const merged: string[] = [];
  for (const name of [...own, ...fromPayload]) {
    if (!merged.includes(name)) merged.push(name);
  }
  return merged;
}

function readData(value: unknown): Record<string, unknown> {
  return parseJsonProp<Record<string, unknown>>(value as Record<string, unknown> | string, {}) ?? {};
}

/**
 * Run the declarations stamped on a wrapper element, minus anything a nested trigger already owns.
 *
 * Wrapper components own a `<div>` around a button that arrives from the payload, so there can be
 * two independent triggers for one click: the component's, on the wrapper, and the payload's, on
 * the button. Both must run. The wrapper used to bail whenever a nested trigger existed, which
 * meant adding an action from the template builder silently switched the component's own behaviour
 * off — the author thinks they added tracking to a CTA and instead they have disabled it.
 *
 * What must not happen is the same action firing twice, so anything the nested trigger declares is
 * dropped from the wrapper's list; me-button runs those. Bubbling puts the nested chain first,
 * which is the order you want anyway when the wrapper's action ends in a navigation. The two chains
 * are not awaited against each other — a nested action that also navigates is a race, and the rule
 * is one navigating action per trigger.
 */
export async function runWrapperActions(
  wrapper: HTMLElement,
  event: Event,
  component?: unknown,
): Promise<void> {
  return runElementActions(wrapper, event, component, undefined, nestedNames(event.target, wrapper));
}

/** Every action name declared between the clicked node and the wrapper, exclusive of the wrapper. */
function nestedNames(target: EventTarget | null, wrapper: HTMLElement): string[] {
  const names: string[] = [];
  // `nodeType`, not `instanceof Element`: this module is also loaded where that global does not
  // exist, and an identity check against a constructor from another realm is fragile anyway.
  let el = (target as Node | null)?.nodeType === 1 ? (target as HTMLElement) : null;
  while (el && el !== wrapper) {
    const declared = el as HTMLElement & { triggerActions?: unknown };
    names.push(
      ...parseActionNames(declared.getAttribute(actionsKey)),
      ...parseActionNames(declared.triggerActions),
    );
    el = el.parentElement;
  }
  return names;
}

async function runSingleAction(
  name: ActionName,
  base: { event: Event; element: HTMLElement; component?: unknown; data: Record<string, unknown> },
  data: Record<string, unknown>,
): Promise<void> {
  const loadAction = ACTION_REGISTRY[name];
  if (!loadAction) {
    console.warn(`Action "${name}" not found in registry`);
    return;
  }

  try {
    const mod = await loadAction();
    const ActionClass = mod.default;
    const context: ActionContext = {
      event: base.event as ActionContext['event'],
      element: base.element,
      component: base.component,
      // Per-action slice, falling back to the whole object — the shape components already rely on.
      data: (data[name] as Record<string, unknown>) ?? data,
    };
    await new ActionClass().execute(context);
  } catch (error) {
    console.error(`Failed to execute action "${name}":`, error);
    throw error;
  }
}
